import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import api from "../../utils/api";

axios.defaults.withCredentials = true;

export interface Employee {
  emp_id: number;
  emp_name: string;
  email: string;
  role: string;
  verified: boolean;
  efficiency: number;
  shift?: string;
  active?: boolean;
  work_location?: string | null;
  is_remote?: boolean;
  is_hybrid?: boolean;
  /** Remote, hybrid, or Super Admin employee — can mark present from portal. */
  can_self_mark?: boolean;
}

interface AuthState {
  employee: Employee | null;
  loading: boolean;
  error: string | null;
  registerSuccess: boolean;
}

const initialState: AuthState = {
  employee: null,
  loading: false,
  error: null,
  registerSuccess: false,
};

interface LoginPayload {
  password: string;
  userRole: string;
}
// --------------------
// Helper type
// --------------------
interface ApiError {
  message: string;
}

// --------------------
// Register
// --------------------
export const registerEmployee = createAsyncThunk(
  "auth/registerEmployee",
  async (data: { email: string; emp_name: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/auth/allocation/register`,
        data,
      );
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError<ApiError>;
      return rejectWithValue(
        err.response?.data?.message || "Registration failed",
      );
    }
  },
);

export const fetchCurrentEmployee = createAsyncThunk(
  "auth/fetchCurrentEmployee",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/auth/allocation/me`,
        { withCredentials: true },
      );
      return res.data.data;
    } catch (error) {
      const err = error as AxiosError<ApiError>;
      return rejectWithValue(
        err.response?.data?.message || "Current employee not found",
      );
    }
  },
);

// --------------------
// Login
// --------------------
export const loginEmployee = createAsyncThunk<
  any,                 // return type (replace with actual response type)
  LoginPayload,        // payload type
  { rejectValue: string }
>(
  "auth/loginEmployee",
  async ({ password, userRole }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/auth/allocation/login`,
        { password, userRole }
      );

      return res.data.data;
    } catch (error) {
      const err = error as AxiosError<ApiError>;
      return rejectWithValue(
        err.response?.data?.message || "Login failed"
      );
    }
  }
);


// --------------------
// Logout
// --------------------
export const logoutEmployee = createAsyncThunk(
  "auth/logoutEmployee",
  async (_, { rejectWithValue }) => {
    try {
      await api.post(
        `/auth/allocation/logout`,
        { withCredentials: true }
      );
      return true;
    } catch (error) {
      const err = error as AxiosError<ApiError>;
      return rejectWithValue(err.response?.data?.message || "Logout failed");
    }
  },
);

export const forceLogout = (dispatch: any) => {
  localStorage.setItem("allocation-logout", Date.now().toString());
  dispatch(logoutEmployee());
};

const authSlice = createSlice({
  name: "allocationAuth",
  initialState,
  reducers: {
    resetAuthState: (state) => {
      state.employee = null;
      state.error = null;
      state.loading = false;
      state.registerSuccess = false;
    },
    setEmployeeOnline: (state, action: PayloadAction<boolean>) => {
      if (state.employee) state.employee.active = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Register
    builder
      .addCase(registerEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerEmployee.fulfilled, (state) => {
        state.loading = false;
        state.registerSuccess = true;
      })
      .addCase(registerEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // CURRENT EMPLOYEE
      .addCase(fetchCurrentEmployee.fulfilled, (state, action) => {
        const next = action.payload;
        const prev = state.employee;
        // Keep the same object reference when nothing meaningful changed so
        // focus/session polls do not re-render the whole employee portal.
        if (
          prev &&
          next &&
          prev.emp_id === next.emp_id &&
          prev.emp_name === next.emp_name &&
          prev.role === next.role &&
          prev.shift === next.shift &&
          prev.active === next.active &&
          prev.verified === next.verified &&
          Boolean(prev.can_self_mark) === Boolean(next.can_self_mark) &&
          Boolean(prev.is_remote) === Boolean(next.is_remote) &&
          Boolean(prev.is_hybrid) === Boolean(next.is_hybrid)
        ) {
          return;
        }
        state.employee = next;
      })
      .addCase(fetchCurrentEmployee.rejected, (state) => {
        state.employee = null;
        state.error = null;
        // 🔥 emit global logout
  localStorage.setItem("allocation-logout", Date.now().toString());
      })

      // Login
      .addCase(loginEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.employee = action.payload;
      })
      .addCase(loginEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Logout
      .addCase(logoutEmployee.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutEmployee.fulfilled, (state) => {
        state.loading = false;
        state.employee = null;
      })
      .addCase(logoutEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetAuthState, setEmployeeOnline } = authSlice.actions;
export default authSlice.reducer;
